import React from "react";
import { NavLink, useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, Sparkles, Users, Briefcase, FileSignature, BookOpen,
  Settings, LogOut, Search, Command, Landmark, Mic, Presentation,
  Calculator, Zap, Globe2, CreditCard, Tag, UsersRound, Telescope, Eye,
  Wand2, Network, FlaskConical, Brain, Package, Crown, Handshake, Layers, Receipt,
  Lock, Bot, ShieldAlert, ShieldCheck, BarChart3, Building2, Menu, X, User, Video,
} from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import NotificationsBell from "@/components/NotificationsBell";

const SECTIONS = [
  {
    label: "Core",
    items: [
      { to: "/", module: "dashboard", label: "Command", icon: LayoutDashboard, eyebrow: "01" },
      { to: "/copilot", module: "copilot", label: "Copilot", icon: Wand2, eyebrow: "02" },
      { to: "/agents", module: "agents_auto", label: "Agents", icon: Bot, eyebrow: "03" },
      { to: "/ai", module: "ai_nexus", label: "AI Nexus", icon: Sparkles, eyebrow: "04" },
      { to: "/research", module: "research", label: "Research", icon: Telescope, eyebrow: "05" },
    ],
  },
  {
    label: "Intelligence",
    items: [
      { to: "/graph", module: "graph", label: "Graph", icon: Network, eyebrow: "06" },
      { to: "/simulation", module: "simulation", label: "Simulation", icon: FlaskConical, eyebrow: "07" },
      { to: "/africa", module: "africa", label: "Africa", icon: Globe2, eyebrow: "08" },
      { to: "/memory", module: "memory", label: "Memory", icon: Brain, eyebrow: "09" },
      { to: "/knowledge", module: "knowledge", label: "Knowledge", icon: BookOpen, eyebrow: "10" },
      { to: "/finance", module: "finance", label: "Finance", icon: Calculator, eyebrow: "11" },
      { to: "/analytics", module: "analytics", label: "Analytics", icon: BarChart3, eyebrow: "12" },
    ],
  },
  {
    label: "Engagement",
    items: [
      { to: "/crm", module: "crm", label: "Relationships", icon: Users, eyebrow: "12" },
      { to: "/engagement", module: "engagement", label: "Engagement Hub", icon: Landmark, eyebrow: "13" },
      { to: "/meetings", module: "meetings", label: "Meetings", icon: Mic, eyebrow: "14" },
      { to: "/diplomacy", module: "diplomacy", label: "Diplomacy", icon: Handshake, eyebrow: "15" },
      { to: "/reputation", module: "reputation", label: "Reputation", icon: Crown, eyebrow: "16" },
    ],
  },
  {
    label: "Execution",
    items: [
      { to: "/operations", module: "operations", label: "Operations", icon: Briefcase, eyebrow: "17" },
      { to: "/proposals", module: "proposals", label: "Proposals", icon: FileSignature, eyebrow: "18" },
      { to: "/studio", module: "studio", label: "Studio", icon: Presentation, eyebrow: "19" },
      { to: "/automation", module: "automation", label: "Automation", icon: Zap, eyebrow: "20" },
      { to: "/finops", module: "finops", label: "FinOps", icon: Receipt, eyebrow: "21" },
      { to: "/vault", module: "vault", label: "Vault", icon: Lock, eyebrow: "22" },
    ],
  },
  {
    label: "System",
    items: [
      { to: "/entities", module: "entities", label: "Entities", icon: Layers, eyebrow: "23" },
      { to: "/marketplace", module: "marketplace", label: "Marketplace", icon: Package, eyebrow: "24" },
      { to: "/team", module: "team", label: "Team", icon: UsersRound, eyebrow: "25" },
      { to: "/organisation", module: "organisation", label: "Organisation", icon: Building2, eyebrow: "25.1" },
      { to: "/platform", module: "platform", label: "Platform", icon: Globe2, eyebrow: "26" },
      { to: "/billing", module: "billing", label: "Billing", icon: CreditCard, eyebrow: "27" },
      { to: "/security", module: "security", label: "Security", icon: ShieldAlert, eyebrow: "28" },
      { to: "/grc", module: "grc", label: "GRC", icon: ShieldCheck, eyebrow: "29" },
      { to: "/settings", module: "governance", label: "Governance", icon: Settings, eyebrow: "30" },
      { to: "/guide", label: "How-to Guide", icon: BookOpen, eyebrow: "31" },
      { to: "/live-meetings", label: "Live Meetings", icon: Video, eyebrow: "31.1" },
      { to: "/profile", label: "My Profile", icon: User, eyebrow: "32" },
    ],
  },
];

export default function Layout({ children }) {
  const { user, logout, hasModule, billing, isInternal, refreshBilling } = useAuth();
  const navigate = useNavigate();
  const [tenantSwitch, setTenantSwitch] = React.useState(localStorage.getItem("nexus_tenant_switch") || "");
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const location = useLocation();

  // Close drawer whenever the route changes
  React.useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Close drawer on Escape key
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setDrawerOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Re-read on route changes (cheap)
  React.useEffect(() => {
    const i = setInterval(() => {
      const cur = localStorage.getItem("nexus_tenant_switch") || "";
      setTenantSwitch((prev) => (prev !== cur ? cur : prev));
    }, 1500);
    return () => clearInterval(i);
  }, []);

  const exitImpersonation = async () => {
    localStorage.removeItem("nexus_tenant_switch");
    setTenantSwitch("");
    toast.success("Returned to Pandora Internal");
    try { await refreshBilling(); } catch (err) { console.error("refreshBilling failed after exit impersonation", err); }
    setTimeout(() => { window.location.href = "/"; }, 400);
  };

  const initials = (user?.name || "U")
    .split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();

  const suiteLabel = isInternal ? "INTERNAL" : (billing?.organization?.suite || "—").toUpperCase();
  const moduleCount = billing?.modules?.length ?? 0;
  const planName = billing?.plan?.name;

  return (
    <div className="min-h-screen flex" data-testid="app-layout">
      {/* Decorative orbs in background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden -z-10">
        <div className="absolute top-[-15%] left-[20%] w-[600px] h-[600px] rounded-full bg-[#D4AF37] opacity-[0.04] blur-[120px]" />
        <div className="absolute bottom-[-20%] right-[10%] w-[700px] h-[700px] rounded-full bg-[#00F0FF] opacity-[0.03] blur-[120px]" />
        <div className="absolute top-[40%] left-[-10%] w-[500px] h-[500px] rounded-full bg-[#0F172A] opacity-[0.5] blur-[100px]" />
      </div>

      {/* Drawer backdrop */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
          onClick={() => setDrawerOpen(false)}
          data-testid="drawer-backdrop"
        />
      )}

      {/* Slide-in drawer sidebar */}
      <aside
        className={`fixed left-0 top-0 bottom-0 w-[280px] nexus-glass-panel z-50 flex flex-col overflow-hidden transition-transform duration-300 ease-out ${drawerOpen ? "translate-x-0" : "-translate-x-full"}`}
        style={{ borderRadius: 0, borderLeft: "none", borderTop: "none", borderBottom: "none" }}
        data-testid="sidebar"
      >
        <div className="p-5 border-b border-white/5 flex items-start justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="sidebar-brand">
            <div className="w-11 h-11 flex items-center justify-center transition-transform group-hover:scale-105"
                 style={{ filter: "drop-shadow(0 0 12px rgba(212,175,55,0.35))" }}>
              <img src="/pandora-nexus-mark.png" alt="Pandora Nexus" className="w-full h-full object-contain" />
            </div>
            <div>
              <div className="font-display text-lg font-semibold tracking-tight text-white leading-none">
                Pandora<span className="text-gradient-gold">Nexus</span>
              </div>
              <div className="label-eyebrow mt-1">Total Institutional Intelligence</div>
            </div>
          </Link>
          <button
            onClick={() => setDrawerOpen(false)}
            data-testid="drawer-close"
            className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/5 shrink-0"
            aria-label="Close menu"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-2.5">
          {SECTIONS.map((sec) => {
            const visibleItems = sec.items.filter((item) => hasModule(item.module));
            if (visibleItems.length === 0) return null;
            return (
              <div key={sec.label} className="mb-4">
                <div className="label-eyebrow px-3 mb-2 mt-1">// {sec.label}</div>
                {visibleItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === "/"}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}-link`}
                    className={({ isActive }) => `nav-link ${isActive ? "nav-link-active" : ""}`}
                  >
                    <item.icon className="w-4 h-4 shrink-0" strokeWidth={1.6} />
                    <span className="font-body text-[13px] flex-1">{item.label}</span>
                    <span className="font-mono text-[10px] tracking-widest opacity-30">{item.eyebrow}</span>
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>

        <div className="p-3 border-t border-white/5 space-y-2">
          <Link
            to="/billing"
            data-testid="suite-pill"
            className="flex items-center justify-between px-3 py-2 rounded-xl bg-white/[0.025] border border-white/5 hover:border-[#D4AF37]/40 hover:bg-white/[0.05] transition-all group"
          >
            <div className="flex items-center gap-2">
              <Tag className="w-3 h-3 text-[#D4AF37]" strokeWidth={1.5} />
              <span className="font-mono text-[10px] tracking-widest text-slate-300 group-hover:text-white">
                SUITE · {suiteLabel}
              </span>
            </div>
            <span className="font-mono text-[10px] tracking-widest text-slate-500 group-hover:text-[#D4AF37]">
              · {moduleCount}M
            </span>
          </Link>

          <div className="flex items-center gap-3 p-2.5 rounded-xl bg-white/[0.03] border border-white/5" data-testid="user-card">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center font-mono text-[11px] font-semibold shrink-0"
              style={{ background: "linear-gradient(135deg, rgba(212,175,55,0.25), rgba(0,240,255,0.15))", color: "#FCE588", border: "1px solid rgba(212,175,55,0.3)" }}
            >
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] text-white truncate font-medium">{user?.name}</div>
              <div className="label-eyebrow mt-0.5">{user?.role}</div>
            </div>
            <button
              onClick={() => { logout(); navigate("/login"); }}
              data-testid="logout-btn"
              aria-label="logout-button"
              className="p-1.5 rounded-md text-slate-500 hover:text-[#D4AF37] hover:bg-white/5 transition-all"
              title="Sign out"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.6} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main column — full width now that sidebar is a drawer */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Global impersonation banner — visible on every page while a tenant switch is active */}
        {tenantSwitch && (
          <div
            className="sticky top-0 z-40 px-6 py-2.5 flex items-center justify-between gap-4 backdrop-blur-xl"
            style={{ background: "rgba(245, 158, 11, 0.12)", borderBottom: "1px solid rgba(245, 158, 11, 0.35)" }}
            data-testid="global-impersonation-banner"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ background: "rgba(245,158,11,0.22)" }}>
                <Eye className="w-3.5 h-3.5 text-amber-300" />
              </div>
              <div className="min-w-0">
                <div className="text-amber-200 text-[12px] font-medium leading-tight">
                  TENANT SWITCH ACTIVE — READ-ONLY · viewing <span className="font-mono text-white">{tenantSwitch}</span>
                </div>
                <div className="text-amber-200/60 text-[10px] leading-tight mt-0.5 hidden md:block">All actions are audit-logged. Writes are blocked.</div>
              </div>
            </div>
            <button
              onClick={exitImpersonation}
              data-testid="global-exit-tenant-btn"
              className="shrink-0 px-3 py-1.5 rounded-md font-mono text-[10px] tracking-widest text-amber-200 hover:bg-amber-500/15 border border-amber-500/40 flex items-center gap-1.5"
            >
              <LogOut className="w-3 h-3" /> EXIT
            </button>
          </div>
        )}
        <header className="sticky top-0 z-30 px-4 md:px-8 py-3 flex items-center gap-4 backdrop-blur-2xl bg-[#05060E]/60 border-b border-white/5" data-testid="top-header" style={{ top: tenantSwitch ? "44px" : 0 }}>
          {/* Hamburger — opens the drawer */}
          <button
            onClick={() => setDrawerOpen(true)}
            data-testid="drawer-open-btn"
            aria-label="Open menu"
            className="shrink-0 p-2 rounded-lg border border-white/10 hover:border-[#D4AF37]/40 hover:bg-white/5 transition-all"
          >
            <Menu className="w-4 h-4 text-slate-300" strokeWidth={1.8} />
          </button>

          {/* Pandora Management Consult logo — header left */}
          <Link to="/" className="shrink-0 flex items-center gap-2 group" data-testid="tenant-logo">
            <img
              src="/pandora-consult-logo.png"
              alt="Pandora Management Consult"
              className="h-9 w-auto object-contain transition-transform group-hover:scale-105"
              style={{ filter: "drop-shadow(0 0 8px rgba(212,175,55,0.25))" }}
            />
          </Link>

          <div className="flex items-center gap-3 flex-1 max-w-xl rounded-full bg-white/[0.04] border border-white/10 hover:border-white/20 transition-colors px-4 py-2">
            <Search className="w-3.5 h-3.5 text-slate-500" strokeWidth={1.8} />
            <input
              data-testid="global-search"
              placeholder="Search intelligence, stakeholders, proposals…"
              className="bg-transparent flex-1 outline-none text-[13px] font-body placeholder:text-slate-600 text-white"
            />
            <div className="hidden md:flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/5 font-mono text-[9px] tracking-widest text-slate-500">
              <Command className="w-2.5 h-2.5" /> K
            </div>
          </div>
          <div className="flex items-center gap-3">
            {planName && (
              <div className="hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#D4AF37]/8 border border-[#D4AF37]/25" data-testid="plan-badge">
                <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]"></span>
                <span className="font-mono text-[10px] tracking-widest text-[#FCE588]">{planName}</span>
              </div>
            )}
            <Link
              to="/about"
              data-testid="header-about-link"
              className="hidden md:inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-white/10 hover:border-white/30 transition-all"
            >
              <span className="font-mono text-[10px] tracking-widest text-slate-300">ABOUT</span>
            </Link>
            <Link
              to="/pricing"
              data-testid="header-pricing-link"
              className="hidden md:inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-[#D4AF37]/40 hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 transition-all"
            >
              <span className="font-mono text-[10px] tracking-widest text-[#D4AF37]">PRICING</span>
            </Link>
            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 glow-pulse" style={{ animationDuration: "3s" }}></span>
              <span className="font-mono text-[10px] tracking-widest text-slate-400">LIVE</span>
            </div>
            <NotificationsBell />
          </div>
        </header>

        <motion.main
          key={window.location.pathname}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="flex-1 overflow-y-auto"
        >
          {children}
        </motion.main>
      </div>
    </div>
  );
}
